<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { onDestroy, onMount } from 'svelte';
	import utc from 'dayjs/plugin/utc';
	import Link from 'svelte-navaid/Link.svelte';
	// Stores
	import SportcastFixtureStore from '@/Stores/Sport/SportcastFixtureStore.js';
	import SportEventStore from '@/Stores/Sport/SportEventStore.js';
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import UrlStore from '@/Stores/UrlStore.js';
	// Components
	import Helmet from '@/Components/Helmet.svelte';
	import Loading from '@/Components/Loading.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Polling from '@/Components/Polling.svelte';
	// Partials
	import LeftMenu from '@/Partials/Sport/Trading/LeftMenu.svelte';
	import EventWithKeyMarkets from '@/Partials/Sport/Event/EventWithKeyMarkets.svelte';
	// Constants
	import { EventStatus } from '@/Constants/Event.js';
	// Services
	import { arrayEquals, paginationBuilder } from '@/Services/Helpers.js';
	import Sockets from '@/Services/Sockets.js';
	import Pagination from '@/Components/Pagination.svelte';

	dayjs.extend(utc);
	MarketGroupStore.resetStore();

	let currentCompetitionId;
	let loading = false;
	let loadingFixtures = [];
	let eventStatusFilter = [EventStatus.Open, EventStatus.InPlay];
	let showStatusFilters = true;

	$: sportId = $UrlStore && $UrlStore.params.get('sport');
	$: compId = $UrlStore && $UrlStore.params.get('comp');
	$: (sportId || !sportId) && (compId || !compId) && getSportEvents(true);
	$: events = $SportEventStore && eventStatusFilter && (sportId || !sportId) && (compId || !compId) && filterEvents();
	$: page = 1;
	$: pageSize = 100;
	$: links = $SportEventStore.count() && paginationBuilder(SportEventStore.getPagination()); //Used for pagination component
	$: numberOfPages =
		($SportEventStore.count() && SportEventStore.getPagination() && SportEventStore.getPagination().total_pages) ?? 0;

	function getSportEvents(showLoading = false) {
		if (showLoading) {
			loading = true;
			currentCompetitionId = false;
			window.scrollTo(0, 0);
		}
		return SportEventStore.getSportEvents(
			{
				sportId: sportId,
				compId: compId,
				status: eventStatusFilter
			},
			true,
			{ size: pageSize, number: page }
		).finally(() => (loading = false));
	}

	function filterEvents() {
		let filtered = $SportEventStore.whereIn('status.id', eventStatusFilter).sortBy('start_time');
		filtered = sportId ? filtered.where('sport.id', parseInt(sportId)) : filtered;
		return compId ? filtered.where('competition.id', parseInt(compId)) : filtered;
	}

	function showNextCompetition(event) {
		if (currentCompetitionId !== event.competition.id) {
			currentCompetitionId = event.competition.id;
			return true;
		}
		return false;
	}

	function getSportcastFixturesForComp(compId) {
		loadingFixtures[compId] = true;
		SportcastFixtureStore.getSportcastFixturesForComp(compId).finally(() => (loadingFixtures[compId] = false));
	}

	function changePage(e) {
		if (e) {
			page = e.detail.page;
			getSportEvents();
		}
	}

	onMount(() => {
		Sockets.subscribeToSport();
	});

	onDestroy(() => {
		Sockets.unsubscribeFromSport();
	});
</script>

<Helmet title="Sport" />

<Polling pollFunction={getSportEvents} frequency={60} />
<div class="p-2 pt-0 bg-white">
	<!-- HEADER -->
	<div class="flex mx-auto space-x-4 max-w-custom">
		<LeftMenu />
		<div class="w-full">
			<!-- EVENT LIST -->
			<div>
				{#if showStatusFilters}
					<div class="my-2 text-center">
						<div class="btn-group">
							<button
								class="btn {arrayEquals(eventStatusFilter, [EventStatus.Open])
									? 'btn-primary-action'
									: 'btn-muted text-dark'}"
								on:click={() => {
									eventStatusFilter = [EventStatus.Open];
									getSportEvents(false);
								}}
							>
								Prematch Only
							</button>
							<button
								class="btn {arrayEquals(eventStatusFilter, [EventStatus.InPlay])
									? 'btn-primary-action'
									: 'btn-muted text-dark'}"
								on:click={() => {
									eventStatusFilter = [EventStatus.InPlay];
									getSportEvents(false);
								}}
							>
								Live Only
							</button>
							<button
								class="btn {arrayEquals(eventStatusFilter, [EventStatus.Open, EventStatus.InPlay])
									? 'btn-primary-action'
									: 'btn-muted text-dark'}"
								on:click={() => {
									eventStatusFilter = [EventStatus.Open, EventStatus.InPlay];
									getSportEvents(false);
								}}
							>
								Prematch & Live
							</button>
						</div>
					</div>
				{/if}
				{#if !compId && !sportId}
					<div class="py-2 font-semibold text-center uppercase">Upcoming</div>
				{/if}
				<div class="px-2">
					{#if !loading}
						{#if events.count()}
							{#if numberOfPages > 1 && compId}
								<Pagination {links} on:click={changePage} className="my-3" />
							{/if}
							{#each events
								.sortBy('start_time')
								.when(!compId, (items) => items.take(pageSize))
								.all() as event, i}
								{#if showNextCompetition(event) || i === 0}
									<div class="flex items-center p-1 -ml-4 space-x-2">
										<div class="font-semibold text-black">
											{event.sport.name} - {event.competition.name}
										</div>
										{#if event.competition.sportcast_competition_id}
											<LoadingButton
												loading={loadingFixtures[compId]}
												className="btn btn-muted text-tiny"
												on:click={getSportcastFixturesForComp(event.competition.id)}
												>Get SGM Events
											</LoadingButton>
										{/if}
									</div>
								{/if}
								<Link
									class="inline-block w-full p-2 mb-2 border cursor-pointer hover:bg-gray-100"
									href={`/sport/event/${event.id}?sport=${event.sport.id}&comp=${event.competition.id}`}
								>
									<EventWithKeyMarkets {event} canRefund={false} />
								</Link>
							{/each}
							{#if numberOfPages > 1 && compId}
								<Pagination {links} on:click={changePage} className="my-3" />
							{/if}
						{:else}
							<div class="py-4 mt-4 font-semibold text-center border">No events available</div>
						{/if}
					{:else}
						<Loading />
					{/if}
				</div>
			</div>
		</div>
	</div>
</div>
